import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KanbanBoard from './components/KanbanBoard';
import TaskInput from './components/TaskInput';
import CountdownTimer from './components/CountdownTimer';
import Calculator from './components/Calculator';
import { FaCalculator } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { taskService } from '../../services/task.service';
import './ProductivityPage.css';

const ProductivityPage = () => {
  const [tasks, setTasks] = useState({
    backlog: [],
    inProgress: [],
    inReview: [],
    completed: []
  });
  const [loading, setLoading] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const calculatorButtonRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    let unsubscribe;

    if (user) {
      unsubscribe = taskService.subscribeToTasks(user.uid, (updatedTasks) => {
        setTasks(updatedTasks);
        setLoading(false);
      });
    }

    return () => unsubscribe && unsubscribe();
  }, [user]);

  const handleAddTask = async (taskData) => {
    try {
      await taskService.createTask(user.uid, taskData);
    } catch (error) {
      console.error('Error adding task:', error);
      // Handle error (show notification, etc.)
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination } = result;

    // Handle deletion
    if (!destination) {
      try {
        const sourceColumn = tasks[source.droppableId];
        const taskToDelete = sourceColumn[source.index];
        
        // Optimistic update for smooth deletion
        setTasks(prev => {
          const newTasks = { ...prev };
          newTasks[source.droppableId].splice(source.index, 1);
          return newTasks;
        });

        await taskService.deleteTask(taskToDelete.id);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
      return;
    }

    // Moving task within the same column or between columns
    try {
      const sourceColumn = tasks[source.droppableId];
      const task = sourceColumn[source.index];

      // Optimistic update for smooth dragging
      setTasks(prev => {
        const newTasks = { ...prev };
        
        // Remove from source
        newTasks[source.droppableId] = Array.from(prev[source.droppableId]);
        const [removed] = newTasks[source.droppableId].splice(source.index, 1);

        // Add to destination
        newTasks[destination.droppableId] = Array.from(prev[destination.droppableId]);
        newTasks[destination.droppableId].splice(destination.index, 0, removed);

        return newTasks;
      });

      // Update in Firebase if column changed
      if (source.droppableId !== destination.droppableId) {
        await taskService.updateTaskStatus(task.id, destination.droppableId);
      }
    } catch (error) {
      console.error('Error moving task:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="productivity-container">
          <div className="loading-spinner">Loading tasks...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="productivity-container">
        <motion.div 
          className="page-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="header-content">
            <div>
              <h1>Productivity</h1>
              <p>Manage your tasks and track progress</p>
            </div>
            <div className="header-controls">
              <button
                ref={calculatorButtonRef}
                className="tool-button"
                onClick={() => setShowCalculator(true)}
                aria-label="Open Calculator"
              >
                <FaCalculator />
              </button>
              <CountdownTimer />
            </div>
          </div>
        </motion.div>

        <TaskInput onAddTask={handleAddTask} />
        <KanbanBoard tasks={tasks} onDragEnd={handleDragEnd} />

        {showCalculator && (
          <Calculator 
            onClose={() => setShowCalculator(false)} 
            buttonRef={calculatorButtonRef}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProductivityPage; 